import {
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
} from "@tanstack/react-router";
import { AIAgentsPage } from "./pages/AIAgentsPage";

import { TestRunsPage } from "./pages/TestRunsPage";
import { DataSourcesPage } from "./pages/DataSourcesPage";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import TestRunDetailsPage from "./pages/TestRunDetailsPage";
import TestDetailsPage from "./pages/TestDetailsPage";
import DataSourceDetailsPage from "./pages/DataSourceDetailsPage";
import { PromptAnalyzerPage } from "./pages/PromptAnalyzerPage";

// Import the new AI agent page components
import {
  OverviewPage,
  TestSuitePage,
  SafetyTestsPage,
  KnowledgePage,
  PerformancePage,
  RunsPage,
  ConfigurationPage,
} from "./pages/ai-agent";
import { TopicDetailsPage } from "./pages/ai-agent/TopicDetailsPage";
import { RecommendationDetailsPage } from "./pages/ai-agent/RecommendationDetailsPage";

// Root route
const rootRoute = createRootRoute({
  component: () => (
    <div className="App">
      <Outlet />
    </div>
  ),
});

// Dashboard route - redirects to AI agents
const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => {
    // Redirect to AI agents page
    window.location.href = "/ai-agents";
    return null;
  },
});

// AI Agents route
const aiAgentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ai-agents",
  component: AIAgentsPage,
});

// AI Agent parent route
const aiAgentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ai-agents/$agentId",
  component: () => <Outlet />,
});

// AI Agent Details routes
const aiAgentOverviewRoute = createRoute({
  getParentRoute: () => aiAgentRoute,
  path: "/",
  component: OverviewPage,
});

const aiAgentTestSuiteRoute = createRoute({
  getParentRoute: () => aiAgentRoute,
  path: "/topics",
  component: TestSuitePage,
});

const aiAgentTopicDetailsRoute = createRoute({
  getParentRoute: () => aiAgentRoute,
  path: "/topics/$topicId",
  component: TopicDetailsPage,
});

const aiAgentSafetyTestsRoute = createRoute({
  getParentRoute: () => aiAgentRoute,
  path: "/safety-tests",
  component: SafetyTestsPage,
});

const aiAgentKnowledgeRoute = createRoute({
  getParentRoute: () => aiAgentRoute,
  path: "/knowledge",
  component: KnowledgePage,
});

const aiAgentPerformanceRoute = createRoute({
  getParentRoute: () => aiAgentRoute,
  path: "/performance",
  component: PerformancePage,
});

const aiAgentRunsRoute = createRoute({
  getParentRoute: () => aiAgentRoute,
  path: "/runs",
  component: RunsPage,
});

const aiAgentConfigurationRoute = createRoute({
  getParentRoute: () => aiAgentRoute,
  path: "/configuration",
  component: ConfigurationPage,
});

const aiAgentRecommendationRoute = createRoute({
  getParentRoute: () => aiAgentRoute,
  path: "/recommendations/$recommendationId",
  component: RecommendationDetailsPage,
});

// Test Runs route
const testRunsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/test-runs",
  component: TestRunsPage,
});

// Test Run Details route
const testRunDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/test-runs/$testRunId",
  component: TestRunDetailsPage,
});

// Test Details route
const testDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/test-runs/$testRunId/test/$testId",
  component: TestDetailsPage,
});

// Integrations route
const integrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/integrations",
  component: IntegrationsPage,
});

// Data Sources route
const dataSourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/data-sources",
  component: DataSourcesPage,
});

// Data Source Details route
const dataSourceDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/data-sources/$dataSourceId",
  component: DataSourceDetailsPage,
});

// Prompt Analyzer route
const promptAnalyzerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/prompt-analyzer",
  component: PromptAnalyzerPage,
});

// Create the route tree
const routeTree = rootRoute.addChildren([
  dashboardRoute,
  aiAgentsRoute,
  aiAgentRoute.addChildren([
    aiAgentOverviewRoute,
    aiAgentTestSuiteRoute,
    aiAgentTopicDetailsRoute,
    aiAgentSafetyTestsRoute,
    aiAgentKnowledgeRoute,
    aiAgentPerformanceRoute,
    aiAgentRunsRoute,
    aiAgentConfigurationRoute,
    aiAgentRecommendationRoute,
  ]),

  testRunsRoute,
  testRunDetailsRoute,
  testDetailsRoute,
  integrationsRoute,
  dataSourcesRoute,
  dataSourceDetailsRoute,
  promptAnalyzerRoute,
]);

// Create the router
export const router = createRouter({ routeTree });

// Declare router types
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
